import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, ArrowDownRight, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../lib/api-client';
import { MainLayout } from '../components/MainLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';

export function TransactionsPage() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    type: undefined as 'ONRAMP' | 'OFFRAMP' | undefined,
    status: undefined as string | undefined,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['transactions', page, filters],
    queryFn: () => api.transactions.list({ page, limit: 20, ...filters }),
  });

  const transactions = data?.data?.transactions || [];
  const pagination = data?.data?.pagination;

  const getStatusBadgeVariant = (status: string): 'success' | 'warning' | 'danger' | 'neutral' => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'PENDING':
      case 'PAYMENT_PENDING':
      case 'PAYOUT_PENDING':
        return 'warning';
      case 'FAILED':
      case 'EXPIRED':
        return 'danger';
      default:
        return 'neutral';
    }
  };

  return (
    <MainLayout>
      <div className="space-y-[var(--space-2xl)]">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[var(--font-size-display-m)] md:text-[var(--font-size-display-l)] font-[var(--font-weight-bold)] text-[var(--fg-1)] font-[var(--font-display)]">
              Transactions
            </h1>
            <p className="text-[var(--font-size-body)] text-[var(--fg-2)] mt-[var(--space-s)]">
              View all your transaction history
            </p>
          </div>
        </div>

        {/* Filters Card */}
        <Card padding="md">
          <div className="flex items-center gap-[var(--space-m)] mb-[var(--space-l)]">
            <Filter size={20} className="text-[var(--fg-3)]" />
            <h3 className="text-[var(--font-size-h3)] font-[var(--font-weight-semibold)] text-[var(--fg-1)]">
              Filters
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-[var(--space-l)]">
            <div className="flex-1">
              <label className="block text-[var(--font-size-label)] font-[var(--font-weight-medium)] text-[var(--fg-2)] mb-[var(--space-s)]">
                Type
              </label>
              <select
                value={filters.type || ''}
                onChange={(e) => setFilters({ ...filters, type: e.target.value as any || undefined })}
                className="input w-full"
              >
                <option value="">All Types</option>
                <option value="ONRAMP">On-Ramp</option>
                <option value="OFFRAMP">Off-Ramp</option>
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-[var(--font-size-label)] font-[var(--font-weight-medium)] text-[var(--fg-2)] mb-[var(--space-s)]">
                Status
              </label>
              <select
                value={filters.status || ''}
                onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
                className="input w-full"
              >
                <option value="">All Statuses</option>
                <option value="COMPLETED">Completed</option>
                <option value="PENDING">Pending</option>
                <option value="FAILED">Failed</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Transactions Table Card */}
        <Card padding="none">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-[var(--space-l)]">
                <div className="w-12 h-12 rounded-full border-4 border-[var(--accent-cyan)] border-t-transparent animate-spin" />
                <p className="text-[var(--fg-3)] text-[var(--font-size-body)]">Loading transactions...</p>
              </div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-[var(--fg-2)] text-[var(--font-size-h3)] font-[var(--font-weight-medium)] mb-[var(--space-s)]">
                  No transactions found
                </p>
                <p className="text-[var(--fg-3)] text-[var(--font-size-body-s)]">
                  Start your first transaction to see it here
                </p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--border-primary)]">
                    <th className="text-left p-[var(--space-l)] font-[var(--font-weight-semibold)] text-[var(--font-size-body-s)] text-[var(--fg-2)] uppercase tracking-wide">
                      Date
                    </th>
                    <th className="text-left p-[var(--space-l)] font-[var(--font-weight-semibold)] text-[var(--font-size-body-s)] text-[var(--fg-2)] uppercase tracking-wide">
                      Type
                    </th>
                    <th className="text-left p-[var(--space-l)] font-[var(--font-weight-semibold)] text-[var(--font-size-body-s)] text-[var(--fg-2)] uppercase tracking-wide">
                      Amount
                    </th>
                    <th className="text-left p-[var(--space-l)] font-[var(--font-weight-semibold)] text-[var(--font-size-body-s)] text-[var(--fg-2)] uppercase tracking-wide">
                      Status
                    </th>
                    <th className="text-left p-[var(--space-l)] font-[var(--font-weight-semibold)] text-[var(--font-size-body-s)] text-[var(--fg-2)] uppercase tracking-wide">
                      ID
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx: any) => (
                    <tr
                      key={tx.id}
                      className="border-b border-[var(--border-primary)] hover:bg-[var(--bg-elevated)] transition-colors duration-[var(--dur)]"
                    >
                      <td className="p-[var(--space-l)] text-[var(--font-size-body)] text-[var(--fg-1)]">
                        <div className="flex flex-col">
                          <span>{new Date(tx.createdAt).toLocaleDateString()}</span>
                          <span className="text-[var(--font-size-body-s)] text-[var(--fg-3)]">
                            {new Date(tx.createdAt).toLocaleTimeString()}
                          </span>
                        </div>
                      </td>
                      <td className="p-[var(--space-l)]">
                        <div className="flex items-center gap-[var(--space-s)]">
                          {tx.type === 'ONRAMP' ? (
                            <div className="w-8 h-8 rounded-[var(--radius-m)] bg-[var(--accent-lime)]/20 flex items-center justify-center">
                              <ArrowUpRight size={16} className="text-[var(--accent-lime)]" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-[var(--radius-m)] bg-[var(--accent-cyan)]/20 flex items-center justify-center">
                              <ArrowDownRight size={16} className="text-[var(--accent-cyan)]" />
                            </div>
                          )}
                          <span className="text-[var(--font-size-body)] text-[var(--fg-1)] font-[var(--font-weight-medium)]">
                            {tx.type === 'ONRAMP' ? 'Buy' : 'Sell'}
                          </span>
                        </div>
                      </td>
                      <td className="p-[var(--space-l)] text-[var(--font-size-body)] text-[var(--fg-1)] font-[var(--font-weight-medium)]">
                        {tx.amountUsd ? `$${Number(tx.amountUsd).toFixed(2)}` : '-'}
                      </td>
                      <td className="p-[var(--space-l)]">
                        <Badge variant={getStatusBadgeVariant(tx.status)} size="md">
                          {tx.status}
                        </Badge>
                      </td>
                      <td className="p-[var(--space-l)] font-[var(--font-mono)] text-[var(--font-size-mono)] text-[var(--fg-3)]">
                        {tx.id.slice(0, 8)}...
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Pagination */}
        {pagination && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-[var(--space-l)]">
            <p className="text-[var(--font-size-body-s)] text-[var(--fg-3)]">
              Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, pagination.total)} of {pagination.total} results
            </p>
            <div className="flex gap-[var(--space-m)]">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
              >
                <ChevronLeft size={16} className="mr-1" />
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.totalPages}
              >
                Next
                <ChevronRight size={16} className="ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
